import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiClock, FiEdit3, FiKey, FiMapPin, FiMonitor, FiNavigation, FiShield, FiSmartphone } from 'react-icons/fi';
import { HiOutlinePaperAirplane } from 'react-icons/hi';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import ProfileSidebar from '../../components/ProfileSidebar/ProfileSidebar';
import UniversalConfirmModal from '../../../components/UniversalConfirmModal/UniversalConfirmModal';
import activityLogService from '../../../services/activityLogService';
import { clearAuth } from '../../../store/slices/authSlice';
import { clearAllUserData } from '../../../utils/logout';

const iconMap = {
  login: <FiKey size={18} />,
  booking: <HiOutlinePaperAirplane size={18} />,
  profile_change: <FiEdit3 size={18} />,
  password_change: <FiShield size={18} />,
  password_reset: <FiAlertTriangle size={18} />,
  security_review: <FiShield size={18} />
};

const badgeMap = {
  login: 'bg-amber-50 text-amber-700 border-amber-200',
  booking: 'bg-sky-50 text-sky-700 border-sky-200',
  profile_change: 'bg-orange-50 text-orange-700 border-orange-200',
  password_change: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  password_reset: 'bg-rose-50 text-rose-700 border-rose-200',
  security_review: 'bg-violet-50 text-violet-700 border-violet-200'
};

const formatDateTime = (value) => {
  if (!value) return 'Unknown time';

  return new Date(value).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getFeedLoginEventId = (entry) => {
  if (entry?.event_id) {
    return entry.event_id;
  }

  if (typeof entry?.id === 'string' && entry.id.startsWith('audit-')) {
    const parsed = Number(entry.id.replace('audit-', ''));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const isSecurityReviewEligible = (entry) => ['login', 'booking', 'password_reset'].includes(entry?.type);
const TAB_KEYS = {
  RECENT_LOGINS: 'recent_logins',
  CHRONOLOGICAL_FEED: 'chronological_feed'
};
const RECENT_LOGINS_PAGE_SIZE = 5;
const FEED_PAGE_SIZE = 10;

const ActivityLoginCard = ({ login, onReview }) => (
  <div className="border border-gray-200 rounded-lg p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center">
        <FiKey size={18} />
      </div>
      <div>
        <div className="font-semibold text-gray-900">{formatDateTime(login.occurred_at)}</div>
        <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
          <span className="inline-flex items-center gap-1">
            <FiMapPin size={14} />
            {login.location || 'Location unavailable'}
          </span>
          <span className="inline-flex items-center gap-1">
            <FiMonitor size={14} />
            {login.browser || 'Unknown browser'}
          </span>
          <span className="inline-flex items-center gap-1">
            <FiSmartphone size={14} />
            {login.device || 'Unknown device'}
          </span>
        </div>
      </div>
    </div>

    {login.reportable && (
      <button
        onClick={() => onReview(login)}
        className="px-4 py-2 text-sm font-semibold rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition-colors self-start md:self-center"
      >
        Not You?
      </button>
    )}
  </div>
);

const ActivityLogPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activityData, setActivityData] = useState({ recentLogins: [], feed: [] });
  const [selectedLogin, setSelectedLogin] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB_KEYS.RECENT_LOGINS);
  const [recentLoginsPage, setRecentLoginsPage] = useState(1);
  const [feedPage, setFeedPage] = useState(1);

  const loadActivity = useCallback(async () => {
    try {
      setLoading(true);
      const response = await activityLogService.getActivityLog();
      setActivityData({
        recentLogins: response.data?.recentLogins || [],
        feed: response.data?.feed || []
      });
    } catch (error) {
      toast.error(error.message || 'Failed to load activity log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActivity();
  }, [loadActivity]);

  const loginSummary = useMemo(() => {
    const reportableLogins = activityData.recentLogins.filter((item) => item.reportable).length;
    const bookings = activityData.feed.filter((item) => item.type === 'booking').length;
    const profileChanges = activityData.feed.filter((item) => item.type === 'profile_change').length;

    return { reportableLogins, bookings, profileChanges };
  }, [activityData]);

  const recentLoginsTotalPages = Math.max(1, Math.ceil(activityData.recentLogins.length / RECENT_LOGINS_PAGE_SIZE));
  const feedTotalPages = Math.max(1, Math.ceil(activityData.feed.length / FEED_PAGE_SIZE));

  const paginatedRecentLogins = useMemo(() => {
    const start = (recentLoginsPage - 1) * RECENT_LOGINS_PAGE_SIZE;
    return activityData.recentLogins.slice(start, start + RECENT_LOGINS_PAGE_SIZE);
  }, [activityData.recentLogins, recentLoginsPage]);

  const paginatedFeed = useMemo(() => {
    const start = (feedPage - 1) * FEED_PAGE_SIZE;
    return activityData.feed.slice(start, start + FEED_PAGE_SIZE);
  }, [activityData.feed, feedPage]);

  const handleTabChange = useCallback((tabKey) => {
    setActiveTab(tabKey);
  }, []);

  const handleRecentLoginsPageChange = useCallback((page) => {
    setRecentLoginsPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleFeedPageChange = useCallback((page) => {
    setFeedPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSecurityReview = async () => {
    if (!selectedLogin) return;

    try {
      setSubmitting(true);
      await activityLogService.reportLogin(selectedLogin.event_id);
      toast.success('Security action taken. Please check your email for password reset steps.');
      clearAllUserData();
      dispatch(clearAuth());
      navigate('/', { replace: true });
    } catch (error) {
      toast.error(error.message || 'Failed to start security review');
    } finally {
      setSubmitting(false);
      setSelectedLogin(null);
    }
  };

  return (
    <div className="bg-jet-gray-100 pt-[40px] pb-8 px-3 sm:px-5">
      <div className="bg-white p-[28px] rounded-[10px] max-w-7xl mx-auto flex flex-col lg:flex-row gap-5">
        <ProfileSidebar />

        <div className="flex-1 bg-white rounded-lg shadow-jet overflow-hidden">
          <div className="bg-[#ffd200] px-5 py-3 border-b border-yellow-200">
            <h2 className="text-lg font-semibold text-black">Account Activity Log</h2>
            <p className="text-sm text-black/70 mt-1">Your last 30 days of login, booking, and profile security activity.</p>
          </div>

          <div className="p-4 sm:p-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jet-yellow mx-auto mb-4"></div>
                  <p className="text-jet-gray-600">Loading activity log...</p>
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="text-sm text-gray-500 mb-1">Recent Logins</div>
                    <div className="text-2xl font-semibold text-gray-900">{activityData.recentLogins.length}</div>
                    <div className="text-xs text-gray-500 mt-1">{loginSummary.reportableLogins} reviewable sign-ins</div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="text-sm text-gray-500 mb-1">Recent Bookings</div>
                    <div className="text-2xl font-semibold text-gray-900">{loginSummary.bookings}</div>
                    <div className="text-xs text-gray-500 mt-1">Captured in the last 30 days</div>
                  </div>
                  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="text-sm text-gray-500 mb-1">Profile Changes</div>
                    <div className="text-2xl font-semibold text-gray-900">{loginSummary.profileChanges}</div>
                    <div className="text-xs text-gray-500 mt-1">Includes password and profile updates</div>
                  </div>
                </div>

                <div className="mb-4 border-b border-gray-200">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleTabChange(TAB_KEYS.RECENT_LOGINS)}
                      className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === TAB_KEYS.RECENT_LOGINS ? 'border-[#ed7d31] text-[#ed7d31]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                      Recent Logins
                    </button>
                    <button
                      onClick={() => handleTabChange(TAB_KEYS.CHRONOLOGICAL_FEED)}
                      className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === TAB_KEYS.CHRONOLOGICAL_FEED ? 'border-[#ed7d31] text-[#ed7d31]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                    >
                      Chronological Feed
                    </button>
                  </div>
                </div>

                {activeTab === TAB_KEYS.RECENT_LOGINS && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <FiClock size={16} className="text-[#ed7d31]" />
                      <h3 className="text-base font-semibold text-gray-900">Recent Logins</h3>
                    </div>
                    <div className="space-y-3">
                      {activityData.recentLogins.length === 0 ? (
                        <div className="border border-dashed border-gray-300 rounded-lg p-6 text-sm text-gray-500">
                          No login activity found in the last 30 days.
                        </div>
                      ) : (
                        paginatedRecentLogins.map((login) => (
                          <ActivityLoginCard
                            key={login.id}
                            login={login}
                            onReview={setSelectedLogin}
                          />
                        ))
                      )}
                    </div>

                    {recentLoginsTotalPages > 1 && (
                      <Pagination
                        currentPage={recentLoginsPage}
                        totalPages={recentLoginsTotalPages}
                        onPageChange={handleRecentLoginsPageChange}
                      />
                    )}
                  </div>
                )}

                {activeTab === TAB_KEYS.CHRONOLOGICAL_FEED && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <FiNavigation size={16} className="text-[#ed7d31]" />
                      <h3 className="text-base font-semibold text-gray-900">Chronological Feed</h3>
                    </div>
                    <div className="space-y-4">
                      {activityData.feed.length === 0 ? (
                        <div className="border border-dashed border-gray-300 rounded-lg p-6 text-sm text-gray-500">
                          No activity recorded yet.
                        </div>
                      ) : (
                      paginatedFeed.map((entry) => (
                        entry.type === 'login' ? (
                          <ActivityLoginCard
                            key={entry.id}
                            login={{
                              id: entry.id,
                              occurred_at: entry.occurred_at,
                              location: entry.location,
                              browser: entry.browser || entry.device_or_browser,
                              device: entry.device || entry.device_or_browser,
                              reportable: Boolean(getFeedLoginEventId(entry)),
                              event_id: getFeedLoginEventId(entry)
                            }}
                            onReview={setSelectedLogin}
                          />
                        ) : (
                        <div key={entry.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${badgeMap[entry.type] || 'bg-gray-50 text-gray-700 border-gray-200'}`}>
                              {iconMap[entry.type] || <FiClock size={18} />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div>
                                <div className="font-semibold text-gray-900">{entry.title}</div>
                                <div className="text-sm text-gray-500">{formatDateTime(entry.occurred_at)}</div>
                              </div>

                              <div className="text-sm text-gray-700 mt-2">{entry.description}</div>

                              {entry.location && (
                                <div className="text-sm text-gray-500 mt-2">Location: {entry.location}</div>
                              )}

                              {entry.device_or_browser && (
                                <div className="text-sm text-gray-500 mt-1">Device/Browser: {entry.device_or_browser}</div>
                              )}

                              {entry.route && (
                                <div className="text-sm text-gray-500 mt-1">Route: {entry.route}</div>
                              )}

                              {entry.amount && (
                                <div className="text-sm text-gray-500 mt-1">Amount: {entry.amount}</div>
                              )}

                              {entry.status && (
                                <div className="text-sm text-gray-500 mt-1">Status: {entry.status}</div>
                              )}

                            </div>
                          </div>
                            <div className="flex flex-col items-start md:items-end gap-3 self-start">
                              {entry.booking_link && (
                                <button
                                  onClick={() => navigate(entry.booking_link)}
                                  className="text-sm font-semibold text-[#ed7d31] hover:underline"
                                >
                                  View booking
                                </button>
                              )}

                              {isSecurityReviewEligible(entry) && getFeedLoginEventId(entry) && (
                                <button
                                  onClick={() => setSelectedLogin({
                                    event_id: getFeedLoginEventId(entry),
                                    id: entry.id,
                                    occurred_at: entry.occurred_at,
                                    location: entry.location,
                                    reportable: true,
                                    type: entry.type,
                                    title: entry.title
                                  })}
                                  className="px-4 py-2 text-sm font-semibold rounded-md border border-red-200 text-red-600 hover:bg-red-50 transition-colors self-start md:self-auto"
                                >
                                  Not You?
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        )
                      ))
                    )}
                    </div>

                    {feedTotalPages > 1 && (
                      <Pagination
                        currentPage={feedPage}
                        totalPages={feedTotalPages}
                        onPageChange={handleFeedPageChange}
                      />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <UniversalConfirmModal
        isOpen={!!selectedLogin}
        onClose={() => !submitting && setSelectedLogin(null)}
        onConfirm={handleSecurityReview}
        closeOnConfirm={false}
        closeOnBackdrop={!submitting}
        disableActions={submitting}
        type="warning"
        title="Start Security Review?"
        message="This will revoke all active sessions, require a password reset, and alert an administrator for review."
        confirmText={submitting ? 'Processing...' : 'Confirm'}
        cancelText="Cancel"
      />
    </div>
  );
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7;

    if (totalPages <= maxVisiblePages + 4) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage > 4) {
        pages.push('...');
      }

      const start = Math.max(2, currentPage - 2);
      const end = Math.min(totalPages - 1, currentPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (currentPage < totalPages - 3) {
        pages.push('...');
      }

      pages.push(totalPages);
    }

    return pages;
  };

  return (
    <div className="flex justify-start items-center gap-1 mt-4 mb-2">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        ‹
      </button>

      {getPageNumbers().map((page, index) => (
        page === '...' ? (
          <span key={`ellipsis-${index}`} className="w-8 h-8 flex items-center justify-center text-gray-400 text-sm">
            ...
          </span>
        ) : (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`w-8 h-8 flex items-center justify-center rounded text-sm transition-colors ${
              currentPage === page
                ? 'bg-[#ed7d31] text-white'
                : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {page}
          </button>
        )
      ))}

      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
      >
        ›
      </button>
    </div>
  );
};

export default ActivityLogPage;
